import { lazy } from 'react'

import {
  OneOnOneEmployeeInfoDocument,
  OneOnOnesPoliciesDocument,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { type NavigationRoutesFn } from 'modules/core/lib/react-router'

const OneOnOnesMeetingPage = lazy(() => import('modules/meetings/pages/OneOnOnes/MeetingPage'))
const OneOnOnesMeetingPrejoinRoomPage = lazy(
  () => import('modules/meetings/pages/OneOnOnes/MeetingPrejoinRoomPage')
)
const OneOnOnesMeetingRoomPage = lazy(
  () => import('modules/meetings/pages/OneOnOnes/MeetingRoomPage')
)
const OneOnOnesRootPage = lazy(() => import('modules/meetings/pages/OneOnOnes/OneOnOnesRootPage'))
const OneOnOnesMeetingWrapperPage = lazy(
  () => import('modules/meetings/pages/OneOnOnes/OneOnOnesMeetingWrapperPage')
)
const EmployeeHeader = lazy(
  () => import('modules/meetings/pages/OneOnOnes/EmployeeMeetingsPage/components/EmployeeHeader')
)
const OneOnOnesOverviewPage = lazy(
  () => import('modules/meetings/pages/OneOnOnes/OneOnOnesOverviewPage')
)
const EmployeeMeetingsPage = lazy(
  () => import('modules/meetings/pages/OneOnOnes/EmployeeMeetingsPage')
)
const OneOnOnesTemplateRedirectPage = lazy(
  () => import('modules/meetings/pages/OneOnOnes/TemplateRedirectPage')
)
const OneOnOnesMeetingRecordPage = lazy(
  () => import('modules/meetings/pages/OneOnOnes/MeetingRecordPage')
)

export const oneOnOnesRoutes: NavigationRoutesFn = (navigation) => {
  return [
    {
      path: navigation.engagement.one_on_ones.path,
      element: <OneOnOnesRootPage />,
      handle: {
        meta: () => ({
          title: i18n.t('engagement.one_on_ones.tab_title'),
        }),
      },
      children: [
        {
          index: true,
          element: <OneOnOnesOverviewPage />,
        },
        {
          path: navigation.engagement.one_on_ones.employeeId.path,
          element: <EmployeeMeetingsPage />,
          handle: {
            resource: true,
            meta: ({ params: { employeeId }, watchQuery }) => {
              return watchQuery({
                query: OneOnOneEmployeeInfoDocument,
                variables: {
                  employeeId: String(employeeId),
                },
              }).map(({ data }) => {
                const canSeeAdvancedMeetings = data?.meetings._policies.seeAdvancedMeetings.can
                const employee = data?.employees.employeesConnection.nodes[0]
                return {
                  title: employee?.fullName,
                  headerComponent: (
                    <EmployeeHeader
                      employee={employee ?? undefined}
                      canSeeAdvancedMeetings={canSeeAdvancedMeetings}
                    />
                  ),
                }
              })
            },
          },
          children: [
            {
              path: navigation.engagement.one_on_ones.employeeId.meetingId.path,
              element: <OneOnOnesMeetingWrapperPage />,
              children: [
                {
                  index: true,
                  element: <OneOnOnesMeetingPage />,
                },
                {
                  path: navigation.engagement.one_on_ones.employeeId.meetingId.prejoin.path,
                  element: <OneOnOnesMeetingPrejoinRoomPage />,
                  handle: {
                    resource: true,
                    meta: ({ params: { employeeId, meetingId }, watchQuery, navigate }) => {
                      return watchQuery({
                        query: OneOnOneEmployeeInfoDocument,
                        variables: {
                          employeeId: String(employeeId),
                        },
                      }).map(({ data }) => {
                        const employee = data?.employees.employeesConnection.nodes[0]
                        const canSeeAdvancedMeetings =
                          data?.meetings._policies.seeAdvancedMeetings.can

                        if (!canSeeAdvancedMeetings)
                          navigate(
                            navigation.engagement.one_on_ones.employeeId.meetingId.fullPath({
                              employeeId: String(employeeId),
                              meetingId: String(meetingId),
                            })
                          )

                        return {
                          title: i18n.t('meetings.prejoin.title'),
                          headerComponent: (
                            <EmployeeHeader
                              employee={employee ?? undefined}
                              canSeeAdvancedMeetings
                            />
                          ),
                        }
                      })
                    },
                  },
                },
                {
                  path: navigation.engagement.one_on_ones.employeeId.meetingId.room.path,
                  element: <OneOnOnesMeetingRoomPage />,
                  handle: {
                    resource: true,
                    meta: ({ params: { employeeId, meetingId }, watchQuery, navigate }) => {
                      return watchQuery({ query: OneOnOnesPoliciesDocument }).map(({ data }) => {
                        if (!data?.meetings._policies.seeAdvancedMeetings.can)
                          navigate(
                            navigation.engagement.one_on_ones.employeeId.meetingId.fullPath({
                              employeeId: String(employeeId),
                              meetingId: String(meetingId),
                            })
                          )
                        return {
                          title: i18n.t('meetings.room.title'),
                        }
                      })
                    },
                  },
                },
                {
                  path: navigation.engagement.one_on_ones.employeeId.meetingId.record.path,
                  element: <OneOnOnesMeetingRecordPage />,
                  handle: {
                    resource: true,
                    meta: ({ params: { employeeId, meetingId }, watchQuery, navigate }) => {
                      return watchQuery({ query: OneOnOnesPoliciesDocument }).map(({ data }) => {
                        if (!data?.meetings._policies.seeAdvancedMeetings.can)
                          navigate(
                            navigation.engagement.one_on_ones.employeeId.meetingId.fullPath({
                              employeeId: String(employeeId),
                              meetingId: String(meetingId),
                            })
                          )
                        return {
                          title: i18n.t('meetings.record.title'),
                        }
                      })
                    },
                  },
                },
              ],
            },
          ],
        },
        {
          path: navigation.engagement.one_on_ones.templateId.path,
          element: <OneOnOnesTemplateRedirectPage />,
          children: [
            {
              path: navigation.engagement.one_on_ones.templateId.prejoin.path,
              element: <OneOnOnesTemplateRedirectPage />,
            },
          ],
        },
      ],
    },
  ]
}
